import { config } from './config.js';
import { sleep } from './utils.js';

/**
 * There is no refresh-token key any more.
 *
 * The refresh token lives in an httpOnly `SameSite=Strict` cookie set by the API on
 * `api.shanksfamily.net`, which shares a registrable domain with this site. JavaScript
 * cannot read it, which is the point: an XSS can ride the session while the page is open
 * but cannot walk away with a seven-day credential. Auth requests therefore send
 * `credentials: 'include'`, and the API's CORS allows credentials against an explicit
 * origin allowlist.
 */

/** A boolean hint, not a credential — see hasSession(). */
const SIGNED_IN_HINT = 'v2.signedIn';

// The API returns 503 + {retryable:true} while a scaled-to-zero Aurora cluster wakes up
// (see api/src/http/response.ts). That is an expected state after an idle period, not a
// fault, so the client absorbs it rather than showing the family an error. Budget is a
// wall-clock deadline, not a sum of sleeps -- the same mistake was made server-side in
// Phase 6 and cost a debugging session; a sum ignores the request time between retries.
const RESUME_DEADLINE_MS = 40_000;
const RESUME_BACKOFF_MS = [1000, 2000, 3000, 5000, 5000, 8000];

/**
 * The API is not consistent about how it returns tokens: /auth/login and /auth/challenge
 * wrap them as {kind:'tokens', tokens}, because those endpoints can also return a
 * challenge instead, while /auth/refresh returns the bare Cognito AuthenticationResult
 * (it has no challenge case to disambiguate). Accept both rather than assuming one --
 * assuming the envelope here logged the user out on every page refresh.
 */
export function extractTokens(payload) {
    if (!payload || typeof payload !== 'object') return null;
    if (payload.kind === 'tokens' && payload.tokens?.AccessToken) return payload.tokens;
    if (payload.AccessToken) return payload;
    return null;
}

export class ApiError extends Error {
    constructor(message, status, body) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.body = body;
    }
}

class ApiClient {
    constructor() {
        // Access token is memory-only and short-lived (5 min). Deliberately NOT persisted:
        // it is the credential that actually opens the API, and a page reload can cheaply
        // mint a new one from the refresh token.
        this.accessToken = null;
        this.refreshPromise = null;
    }

    /**
     * One-time cleanup of the pre-cookie storage.
     *
     * Every browser that used the portal before the cutover still holds a `v2.refreshToken`
     * in localStorage — a live seven-day credential, readable by script, which is precisely
     * what moving to a cookie was meant to eliminate. Removing it on boot is the only way to
     * reach those copies. Safe to delete along with this comment once the family has all
     * signed in again.
     */
    purgeLegacyRefreshToken() {
        try {
            localStorage.removeItem('v2.refreshToken');
        } catch {
            // Storage refused (private browsing); nothing was stored there either.
        }
    }

    setSession(tokens) {
        // Only the access token is ours to hold. The refresh token arrives as an httpOnly
        // cookie the browser stores and this code can neither read nor write.
        this.accessToken = tokens.AccessToken ?? null;
        try {
            localStorage.setItem(SIGNED_IN_HINT, '1');
        } catch { /* storage refused; hasSession falls back to trying */ }
    }

    clearSession() {
        this.accessToken = null;
        this.refreshPromise = null;
        // The cookie is cleared by POST /auth/logout, which is the only thing that can.
        try {
            localStorage.removeItem(SIGNED_IN_HINT);
        } catch { /* nothing was stored either */ }
    }

    /**
     * Whether a session is worth trying to restore.
     *
     * The refresh token is now unreadable by script, so this cannot check for it. Returning
     * `true` unconditionally worked but made every anonymous visitor pay for a doomed
     * refresh round-trip before the page rendered — including guests opening an event page
     * from a QR code on venue wifi, which is the one page that most needs to be quick.
     *
     * So this keeps a boolean hint instead. It is not a credential and grants nothing: at
     * worst a stale `true` costs the same wasted request this avoids, and the API is the
     * only thing that decides whether the cookie is real.
     */
    hasSession() {
        try {
            return localStorage.getItem(SIGNED_IN_HINT) === '1';
        } catch {
            // Storage refused: fall back to trying, rather than locking someone out.
            return true;
        }
    }

    /**
     * Single place every call goes through: bearer injection, one transparent token
     * refresh on 401, and the Aurora-resume retry.
     */
    async request(path, { method = 'GET', body, auth = true, eventToken, asText = false, _retried = false } = {}) {
        const deadline = Date.now() + RESUME_DEADLINE_MS;
        let attempt = 0;

        for (;;) {
            const headers = {};
            if (body !== undefined) headers['content-type'] = 'application/json';
            if (auth) {
                if (!this.accessToken) await this.refreshSession();
                if (this.accessToken) headers.authorization = `Bearer ${this.accessToken}`;
            }
            // Event guests are not Cognito users and carry a different credential in its
            // own header. It never travels with a Bearer token -- the two identify
            // different kinds of caller and the API reads exactly one of them per route.
            if (eventToken) headers['x-event-token'] = eventToken;

            const res = await fetch(`${config.apiUrl}${path}`, {
                method,
                headers,
                // Only the auth routes. The cookie is Path=/auth so it would not be
                // attached elsewhere anyway, but asking for credentials on a request needs
                // a credentialed CORS preflight, and those follow stricter matching rules --
                // keeping the other forty-odd routes out of that mode removes a whole class
                // of failure for no loss.
                credentials: path.startsWith('/auth/') ? 'include' : 'omit',
                body: body === undefined ? undefined : JSON.stringify(body),
            });

            // A successful reply that is a file (the .ics download) is text, not JSON. Errors are
            // JSON either way, so they still go through the parser below.
            const payload = asText && res.ok ? await res.text() : await this.#parseBody(res);

            if (res.status === 503 && payload?.retryable) {
                const wait = RESUME_BACKOFF_MS[Math.min(attempt, RESUME_BACKOFF_MS.length - 1)];
                // Check when the sleep would *finish*, so the budget can't be overrun.
                if (Date.now() + wait > deadline) {
                    throw new ApiError(payload.error ?? 'The service is still waking up', 503, payload);
                }
                attempt += 1;
                await sleep(wait);
                continue;
            }

            // One retry only: if a freshly minted token is still refused, the session is
            // genuinely gone and retrying would loop.
            if (res.status === 401 && auth && !_retried) {
                this.accessToken = null;
                const refreshed = await this.refreshSession();
                if (refreshed) {
                    return this.request(path, { method, body, auth, asText, _retried: true });
                }
                this.clearSession();
                throw new ApiError('Your session has expired -- please sign in again', 401, payload);
            }

            if (!res.ok) {
                throw new ApiError(payload?.error ?? `Request failed (${res.status})`, res.status, payload);
            }

            return payload;
        }
    }

    async #parseBody(res) {
        const text = await res.text();
        if (!text) return null;
        try {
            return JSON.parse(text);
        } catch {
            return { error: text };
        }
    }

    /** Exchanges the stored refresh token for a new access token. Concurrent callers share one flight. */
    async refreshSession() {
        if (this.refreshPromise) return this.refreshPromise;

        this.refreshPromise = (async () => {
            try {
                const data = await this.request('/auth/refresh', {
                    method: 'POST',
                    auth: false,
                });
                const tokens = extractTokens(data);
                if (!tokens) return false;
                // Rotation is enabled on the pool, so a refresh usually returns a new
                // refresh token -- the API replaces the cookie, and nothing here has to
                // (or can) do anything about it.
                this.setSession(tokens);
                return true;
            } catch {
                // The hint was stale (cookie expired or cleared server-side).
                try { localStorage.removeItem(SIGNED_IN_HINT); } catch { /* nothing to clear */ }
                return false;
            } finally {
                this.refreshPromise = null;
            }
        })();

        return this.refreshPromise;
    }

    // ---- Auth ----------------------------------------------------------------

    login(email, password) {
        return this.request('/auth/login', { method: 'POST', body: { email, password }, auth: false });
    }

    respondToChallenge(payload) {
        return this.request('/auth/challenge', { method: 'POST', body: payload, auth: false });
    }

    logout() {
        return this.request('/auth/logout', { method: 'POST' });
    }

    /**
     * Asks the API to wake the database. Unauthenticated, and called on page load before anyone
     * signs in -- the cluster sleeps after a quiet spell and takes ~30s to come back, which is
     * better spent while someone reads the page than while they watch a spinner.
     */
    warmup() {
        return this.request('/warmup', { auth: false });
    }

    forgotPassword(email) {
        return this.request('/auth/forgot-password', { method: 'POST', body: { email }, auth: false });
    }

    resetPassword(email, code, newPassword) {
        return this.request('/auth/reset-password', {
            method: 'POST',
            body: { email, code, newPassword },
            auth: false,
        });
    }

    // ---- Profile & MFA -------------------------------------------------------

    getProfile() {
        return this.request('/users/profile');
    }

    updateProfile(data) {
        return this.request('/users/profile', { method: 'PUT', body: data });
    }

    changePassword(oldPassword, newPassword) {
        return this.request('/users/password', { method: 'PUT', body: { oldPassword, newPassword } });
    }

    getMfaStatus() {
        return this.request('/users/mfa/status');
    }

    setupMfa() {
        return this.request('/users/mfa/setup', { method: 'POST' });
    }

    verifyMfaSetup(code) {
        return this.request('/users/mfa/verify-setup', { method: 'POST', body: { code } });
    }

    /**
     * Confirms the code Cognito mails to a new address. An email change is not applied
     * until this succeeds -- until then the account keeps its previous address.
     */
    verifyProfileEmail(code) {
        return this.request('/users/profile/email/verify', { method: 'POST', body: { code } });
    }

    /** Requires the current password: turning off a second factor is not a session-only act. */
    disableMfa(currentPassword) {
        return this.request('/users/mfa/disable', { method: 'POST', body: { currentPassword } });
    }

    // ---- Gallery -------------------------------------------------------------

    listImages(params = {}) {
        const qs = new URLSearchParams(params).toString();
        return this.request(`/gallery/images${qs ? `?${qs}` : ''}`);
    }

    listSharedImages() {
        return this.request('/gallery/shared');
    }

    listCollections() {
        return this.request('/gallery/collections');
    }

    listShareTargets() {
        return this.request('/gallery/share-targets');
    }

    createUpload(meta) {
        return this.request('/gallery/images', { method: 'POST', body: meta });
    }

    updateImage(id, data) {
        return this.request(`/gallery/images/${id}`, { method: 'PUT', body: data });
    }

    deleteImage(id) {
        return this.request(`/gallery/images/${id}`, { method: 'DELETE' });
    }

    getDownloadUrl(id) {
        return this.request(`/gallery/images/${id}/download`);
    }

    copyImage(id) {
        return this.request(`/gallery/images/${id}/copy`, { method: 'POST' });
    }

    getShares(id) {
        return this.request(`/gallery/images/${id}/shares`);
    }

    setShares(id, userIds) {
        return this.request(`/gallery/images/${id}/shares`, { method: 'PUT', body: { userIds } });
    }

    /**
     * Uploads bytes straight to S3 with the presigned POST from createUpload(). This
     * bypasses the API entirely -- API Gateway caps payloads at 10MB and Lambda responses
     * at 6MB, so a 32MB photo could never travel through the API the way it did in v1.
     * XHR rather than fetch because fetch still cannot report upload progress.
     */
    uploadToS3({ url, fields }, file, onProgress) {
        return new Promise((resolve, reject) => {
            const form = new FormData();
            for (const [k, v] of Object.entries(fields)) form.append(k, v);
            form.append('file', file);

            const xhr = new XMLHttpRequest();
            xhr.open('POST', url);
            xhr.upload.addEventListener('progress', (e) => {
                if (e.lengthComputable) onProgress?.(e.loaded / e.total);
            });
            xhr.addEventListener('load', () => {
                // S3 returns 204 for a successful presigned POST.
                if (xhr.status >= 200 && xhr.status < 300) resolve();
                else reject(new ApiError(`Upload failed (${xhr.status})`, xhr.status, xhr.responseText));
            });
            xhr.addEventListener('error', () => reject(new ApiError('Upload failed', 0, null)));
            xhr.send(form);
        });
    }

    // ---- Admin ---------------------------------------------------------------

    listUsers() {
        return this.request('/admin/users');
    }

    createUser(data) {
        return this.request('/admin/users', { method: 'POST', body: data });
    }

    getUser(id) {
        return this.request(`/admin/users/${id}`);
    }

    updateUser(id, data) {
        return this.request(`/admin/users/${id}`, { method: 'PUT', body: data });
    }

    deleteUser(id) {
        return this.request(`/admin/users/${id}`, { method: 'DELETE' });
    }

    /** Admin MFA reset — the only recovery path for a lost authenticator (v2 has no backup codes). */
    disableUserMfa(id) {
        return this.request(`/admin/users/${id}/mfa/disable`, { method: 'POST' });
    }

    // ---------------------------------------------------------------- Calendar

    /** Events the caller may see with each one's occurrences inside [from, to). */
    listCalendarEvents(from, to) {
        const query = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() });
        return this.request(`/calendar/events?${query}`);
    }

    getCalendarEvent(id) {
        return this.request(`/calendar/events/${id}`);
    }

    createCalendarEvent(data) {
        return this.request('/calendar/events', { method: 'POST', body: data });
    }

    updateCalendarEvent(id, data) {
        return this.request(`/calendar/events/${id}`, { method: 'PUT', body: data });
    }

    deleteCalendarEvent(id) {
        return this.request(`/calendar/events/${id}`, { method: 'DELETE' });
    }

    /** The caller's feed address, or nulls when they have none. */
    getCalendarToken() {
        return this.request('/calendar/token');
    }

    /** Creating and regenerating are one act: the old address stops working as the new one starts. */
    createCalendarToken() {
        return this.request('/calendar/token', { method: 'POST' });
    }

    revokeCalendarToken() {
        return this.request('/calendar/token', { method: 'DELETE' });
    }

    /** One event as iCalendar text. Fetched with the bearer token, so it cannot be a plain link. */
    getEventIcs(id) {
        return this.request(`/cal/event/${id}.ics`, { asText: true });
    }

    // ---------------------------------------------------------------- Events (organiser)

    listEvents() {
        return this.request('/events');
    }

    createEvent(data) {
        return this.request('/events', { method: 'POST', body: data });
    }

    getEvent(id) {
        return this.request(`/events/${id}`);
    }

    updateEvent(id, data) {
        return this.request(`/events/${id}`, { method: 'PUT', body: data });
    }

    deleteEvent(id) {
        return this.request(`/events/${id}`, { method: 'DELETE' });
    }

    setEventAdmins(id, userIds) {
        return this.request(`/events/${id}/admins`, { method: 'PUT', body: { user_ids: userIds } });
    }

    createEventBackgroundUpload(id) {
        return this.request(`/events/${id}/background`, { method: 'POST' });
    }

    deleteEventBackground(id) {
        return this.request(`/events/${id}/background`, { method: 'DELETE' });
    }

    listEventGuests(id) {
        return this.request(`/events/${id}/guests`);
    }

    blockEventGuest(id, guestId, blocked) {
        return this.request(`/events/${id}/guests/${guestId}/block`, { method: 'POST', body: { blocked } });
    }

    listEventMedia(id) {
        return this.request(`/events/${id}/media`);
    }

    deleteEventMedia(id, mediaId) {
        return this.request(`/events/${id}/media/${mediaId}`, { method: 'DELETE' });
    }

    listEventGuestbook(id) {
        return this.request(`/events/${id}/guestbook`);
    }

    deleteEventGuestbookEntry(id, entryId) {
        return this.request(`/events/${id}/guestbook/${entryId}`, { method: 'DELETE' });
    }

    // ---------------------------------------------------------------- Events (guest)
    //
    // Every call below passes auth:false. A guest has no Cognito session, and letting the
    // client try to refresh one would turn "your event access ended" into a spurious
    // "please sign in again" on a page that has no sign-in.

    // Guest-route credential. `{ eventToken }` for someone who confirmed a code,
    // `{ session: true }` for a signed-in family member. Never both, and never a guessed
    // default: falling back to auth:true for a stranger would trigger a refresh attempt
    // and, on its failure, clear a session that a real user might be holding.
    #eventAuth(credential) {
        return credential?.session
            ? { auth: true }
            : { auth: false, eventToken: credential?.eventToken };
    }

    getPublicEvent(slug) {
        return this.request(`/events/public/${encodeURIComponent(slug)}`, { auth: false });
    }

    registerEventGuest(slug, data) {
        return this.request(`/events/public/${encodeURIComponent(slug)}/register`, {
            method: 'POST', body: data, auth: false,
        });
    }

    verifyEventGuest(slug, data) {
        return this.request(`/events/public/${encodeURIComponent(slug)}/verify`, {
            method: 'POST', body: data, auth: false,
        });
    }

    getEventFeed(slug, credential, since = 0) {
        const query = since > 0 ? `?since=${since}` : '';
        return this.request(`/events/public/${encodeURIComponent(slug)}/feed${query}`,
            this.#eventAuth(credential));
    }

    createEventUpload(slug, credential, data) {
        return this.request(`/events/public/${encodeURIComponent(slug)}/uploads`, {
            method: 'POST', body: data, ...this.#eventAuth(credential),
        });
    }

    postEventGuestbook(slug, credential, data) {
        return this.request(`/events/public/${encodeURIComponent(slug)}/guestbook`, {
            method: 'POST', body: data, ...this.#eventAuth(credential),
        });
    }

    /**
     * Where this visitor's own uploads have got to in their type check and malware scan:
     * `{ media: { [id]: status }, guestbook: { [id]: status } }`, each 'checking', 'ready' or
     * 'failed'. One call for everything the page is waiting on, because every guest at a
     * venue shares one IP address and so one rate limit.
     */
    getEventUploadStatus(slug, credential, { media = [], guestbook = [] } = {}) {
        const query = new URLSearchParams();
        if (media.length) query.set('media', media.join(','));
        if (guestbook.length) query.set('guestbook', guestbook.join(','));
        return this.request(`/events/public/${encodeURIComponent(slug)}/uploads/status?${query}`,
            this.#eventAuth(credential));
    }
}

export const api = new ApiClient();
